import { useEffect, useMemo, useState } from 'react';
import { apiGet, apiPatch, ApiError } from '../lib/apiClient';
import { useApiResource } from '../lib/useApiResource';
import { DataTable } from '../components/DataTable';
import { StatusBadge } from '../components/StatusBadge';
import { Switch } from '../components/FormCheck';

/**
 * Admin control over which app surfaces render (docs/administration.md §24.2.6).
 *
 * Two data sources joined on the surface id: the placement registry (what an
 * admin controls) and the feature registry's 30-day usage (what the module is
 * actually worth). Seeing them together is the point — a "hide this" decision
 * should be made next to the number that justifies it.
 *
 * Usage is best-effort: a feature with no events yet shows an em dash rather
 * than blocking the page, since sub-project E's instrumentation may not cover
 * every surface (§24.7). The switches must keep working when the analytics read
 * fails, so it is a separate, non-blocking fetch.
 */

/** Mirrors the backend's SurfaceTier, which is `user.lynkPulse.plan` verbatim. */
type SurfaceTier = 'free' | 'plus' | 'guardian';
const ALL_TIERS: SurfaceTier[] = ['free', 'plus', 'guardian'];

interface Surface {
  id: string;
  host: string;
  nameEn: string;
  nameEs: string;
  visible: boolean;
  order: number;
  tiers: SurfaceTier[];
  /** Required surfaces cannot be hidden — the backend 400s, so the switch is disabled. */
  required: boolean;
  config: Record<string, unknown>;
}

type FeatureVerdict = 'hot' | 'warm' | 'cold';

interface FeatureSummary {
  featureId: string;
  eventCount: number;
  verdict: FeatureVerdict;
}

/* Same labels and tones as the Analytics section, so one feature reads identically
   on both screens. */
const VERDICT_LABEL: Record<FeatureVerdict, string> = {
  hot: '🔥 Hot',
  warm: '🟡 Warm',
  cold: '🧊 Cold',
};

const VERDICT_TONE: Record<FeatureVerdict, 'good' | 'warn' | 'bad'> = {
  hot: 'good',
  warm: 'warn',
  cold: 'bad',
};

const hostLabel = (host: string) =>
  host
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export function SurfaceControlsPage() {
  const { data: surfaces, loading, error, reload } = useApiResource(
    () => apiGet<{ surfaces: Surface[] }>('/admin/surfaces').then((res) => res.surfaces ?? []),
    [],
  );

  const [usage, setUsage] = useState<Record<string, FeatureSummary>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pendingVisible, setPendingVisible] = useState<Record<string, boolean>>({});

  /* Usage is deliberately not routed through useApiResource: its failure must not
     surface as the page's error, because the controls remain fully usable without it. */
  useEffect(() => {
    apiGet<FeatureSummary[]>('/analytics/admin/features')
      .then((list) => setUsage(Object.fromEntries((list ?? []).map((f) => [f.featureId, f]))))
      .catch(() => setUsage({}));
  }, []);

  useEffect(() => {
    if (!surfaces || Object.keys(pendingVisible).length === 0) return;
    setPendingVisible((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const id of Object.keys(prev)) {
        const s = surfaces.find((row) => row.id === id);
        if (s && s.visible === prev[id]) {
          delete next[id];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [surfaces, pendingVisible]);

  /** Optimistic state wins until the reload confirms it (see the effect above). */
  const effectiveVisible = (s: Surface) =>
    s.id in pendingVisible ? pendingVisible[s.id]! : s.visible;

  const byHost = useMemo(() => {
    const groups: Record<string, Surface[]> = {};
    for (const s of surfaces ?? []) (groups[s.host] ??= []).push(s);
    /* Ties on `order` are legal — an admin can give two surfaces the same number — so the
       id breaks them, keeping the list stable across reads instead of leaving it to sort
       implementation details. */
    for (const list of Object.values(groups)) {
      list.sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
    }
    return groups;
  }, [surfaces]);

  const toggleVisible = async (surface: Surface, visible: boolean) => {
    setPendingVisible((prev) => ({ ...prev, [surface.id]: visible }));
    setBusyId(surface.id);
    setActionError(null);
    try {
      await apiPatch(`/admin/surfaces/${encodeURIComponent(surface.id)}`, { visible });
      reload();
    } catch (err) {
      // Roll back to the server's value so the switch never lies.
      setActionError(err instanceof ApiError ? err.message : `Failed to update ${surface.id}`);
      setPendingVisible((prev) => {
        const next = { ...prev };
        delete next[surface.id];
        return next;
      });
    } finally {
      setBusyId(null);
    }
  };

  const toggleTier = async (surface: Surface, tier: SurfaceTier) => {
    const current = surface.tiers ?? ALL_TIERS;
    const next = current.includes(tier)
      ? current.filter((t) => t !== tier)
      : [...current, tier];
    // The backend rejects an empty tiers array; block it here so the admin gets an
    // explanation instead of a 400.
    if (next.length === 0) {
      setActionError('A surface must be available to at least one tier. Hide it instead.');
      return;
    }
    setBusyId(surface.id);
    setActionError(null);
    try {
      await apiPatch(`/admin/surfaces/${encodeURIComponent(surface.id)}`, { tiers: next });
      reload();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : `Failed to update ${surface.id}`);
    } finally {
      setBusyId(null);
    }
  };

  const showLoadingState = !surfaces && loading;
  const hosts = Object.keys(byHost).sort();

  return (
    <div>
      <div className="section-header">
        <div>
          <h1>Surface Controls</h1>
          <p>
            Which modules render in the app, in what order, and for which LynkPulse tiers.
            Tier selection controls what is <em>drawn</em>, not what is authorized — anything
            that must actually be withheld is gated by the backend, not here.
          </p>
        </div>
        <button type="button" className="btn" onClick={reload}>
          Refresh
        </button>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {actionError && <div className="error-banner">{actionError}</div>}

      {showLoadingState ? (
        <div className="empty-state">Loading surfaces…</div>
      ) : hosts.length === 0 ? (
        <div className="empty-state">No surfaces are registered in the backend manifest.</div>
      ) : (
        hosts.map((host) => {
          const list = byHost[host];
          return (
            <div className="form-card" key={host} style={{ maxWidth: 'none', marginBottom: '1rem' }}>
              <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>
                {hostLabel(host)}
              </h2>

              {/* Hiding an entire host is a supported incident action, so this warns rather
                  than blocks — the backend does not reject it either (§24.2.7). */}
              {list.every((s) => !effectiveVisible(s)) && (
                <div role="status" className="warning-banner">
                  Every surface in this host is hidden. The screen will render empty for all
                  users.
                </div>
              )}

              <DataTable
                id={`surfaces-${host}`}
                rows={list}
                getRowKey={(s) => s.id}
                controls={false}
                pageSize={null}
                columns={[
                  {
                    key: 'surface',
                    header: 'Surface',
                    value: (s) => s.nameEn,
                    render: (s) => (
                      <div>
                        <div>{s.nameEn}</div>
                        <div className="cell-sub">{s.id}</div>
                      </div>
                    ),
                  },
                  { key: 'order', header: 'Order', value: (s) => s.order, width: '80px', align: 'right' },
                  {
                    key: 'tiers',
                    header: 'Tiers',
                    render: (s) => (
                      <div className="row-actions">
                        {ALL_TIERS.map((tier) => {
                          const on = (s.tiers ?? ALL_TIERS).includes(tier);
                          return (
                            <button
                              key={tier}
                              type="button"
                              className={on ? 'btn btn-xs btn-primary' : 'btn btn-xs'}
                              aria-pressed={on}
                              disabled={busyId === s.id}
                              onClick={() => void toggleTier(s, tier)}
                            >
                              {tier}
                            </button>
                          );
                        })}
                      </div>
                    ),
                  },
                  {
                    key: 'usage',
                    header: '30-day usage',
                    value: (s) => usage[s.id]?.eventCount ?? null,
                    render: (s) => {
                      const stats = usage[s.id];
                      if (!stats) return <span className="cell-sub">—</span>;
                      return (
                        <div className="row-actions" style={{ alignItems: 'center' }}>
                          <span>{stats.eventCount}</span>
                          <StatusBadge
                            label={VERDICT_LABEL[stats.verdict]}
                            tone={VERDICT_TONE[stats.verdict]}
                          />
                        </div>
                      );
                    },
                  },
                  {
                    key: 'visible',
                    header: 'Visible',
                    width: '90px',
                    render: (s) => (
                      /* The title sits on the wrapper, not the input: a disabled input
                         fires no pointer events, so its own tooltip would never show. */
                      <span
                        title={
                          s.required
                            ? 'This surface is required and cannot be hidden'
                            : undefined
                        }
                      >
                        <Switch
                          checked={effectiveVisible(s)}
                          disabled={s.required || busyId === s.id}
                          tone="success"
                          ariaLabel={`Show ${s.nameEn}`}
                          onChange={(next) => void toggleVisible(s, next)}
                        />
                      </span>
                    ),
                  },
                ]}
              />
            </div>
          );
        })
      )}
    </div>
  );
}
